import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AppData,
  defaultData,
  resetData as resetLocalData,
  checkAndResetForNewDay,
  checkAndResetTimedTasks,
  checkAndResetDailyRewards,
  checkAndUnlockAchievements,
  getCurrentTimestamp,
  syncWalletBalance,
  migrateDataSchema
} from '@/utils/storage';
import { useFirebase } from '@/providers/FirebaseProvider';
import { NativeFeatures } from '@/services/nativeFeatures';

export const useStarData = () => {
  const [data, setData] = useState<AppData | null>(null);
  const dataRef = useRef<AppData | null>(null);
  const [loading, setLoading] = useState(true);
  const {
    loadData: loadFirebaseData,
    saveData: saveFirebaseData,
    user,
    listenToFamilyData
  } = useFirebase();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Load from both native storage and Firebase to get the most recent data
      const [nativeData, firebaseData] = await Promise.all([
        NativeFeatures.getFromNativeStorage('star-chore-data'),
        loadFirebaseData()
      ]);

      // Use the most recently synced data, or fallback to either source
      let appData: AppData | null = null;
      if (nativeData && firebaseData) {
        // Compare lastSync timestamps to determine which is newer
        const nativeSync = nativeData.lastSync ? new Date(nativeData.lastSync).getTime() : 0;
        const firebaseSync = firebaseData.lastSync ? new Date(firebaseData.lastSync).getTime() : 0;
        appData = nativeSync >= firebaseSync ? nativeData : firebaseData;
        console.log('[useStarData] Using', nativeSync >= firebaseSync ? 'native' : 'firebase', 'data (newer)');
      } else {
        appData = nativeData || firebaseData;
        console.log('[useStarData] Using', nativeData ? 'native' : 'firebase', 'data (only source)');
      }

      // Migrate data schema to ensure all required fields exist (handles old data)
      const currentData = appData ? migrateDataSchema(appData) : defaultData;
      
      // Check and reset for new day FIRST (includes daily tasks and rewards)
      let updatedData = checkAndResetForNewDay(currentData);
      
      // Then check and reset interval-based tasks
      updatedData = checkAndResetTimedTasks(updatedData);
      
      // Finally check daily rewards (in case some weren't caught by checkAndResetForNewDay)
      updatedData = checkAndResetDailyRewards(updatedData);
      
      // If data was updated, save it back to both native storage and Firebase
      if (updatedData !== currentData) {
        try {
          await NativeFeatures.saveToNativeStorage('star-chore-data', updatedData);
          await saveFirebaseData(updatedData);
          console.log('[useStarData] fetchData - Page load resets applied and saved');
        } catch (saveError) {
          console.error('[useStarData] fetchData - Error saving reset data:', saveError);
          // Continue with updated data even if save fails
        }
      }
      
      setData(updatedData);
      setLoading(false);
    } catch (error) {
      console.error('[useStarData] fetchData - Error loading data:', error);
      setData(defaultData);
      setLoading(false);
    }
  }, [loadFirebaseData, saveFirebaseData, user]);

  const updateData = useCallback(async (newData: AppData) => {
    const checkedData = checkAndUnlockAchievements(newData);
    const syncedData = syncWalletBalance(checkedData);

    // Add lastSync timestamp to track when data was last saved
    const dataToSave = {
      ...syncedData,
      lastSync: getCurrentTimestamp()
    };

    setData(dataToSave);

    try {
      // Save to both native storage and Firebase with sync timestamp
      await NativeFeatures.saveToNativeStorage('star-chore-data', dataToSave);
      await saveFirebaseData(dataToSave);
      console.log('[useStarData] updateData - Data saved successfully to both storages');
    } catch (error) {
      console.error('[useStarData] updateData - Error saving data:', error);
      throw error; // Re-throw to allow callers to handle the error
    }

    return dataToSave;
  }, [saveFirebaseData, user]);

  const resetAppData = useCallback(async () => {
    const freshData = {
      ...resetLocalData(),
      lastSync: getCurrentTimestamp()
    };
    setData(freshData);
    try {
      await NativeFeatures.saveToNativeStorage('star-chore-data', freshData);
      await saveFirebaseData(freshData);
      console.log('[useStarData] resetAppData - Data reset and saved successfully to both storages');
    } catch (error) {
      console.error('[useStarData] resetAppData - Error calling saveFirebaseData:', error);
      throw error;
    }
    return freshData;
  }, [saveFirebaseData]);

  const updateStars = useCallback(async (newStars: number) => {
    const baseData = dataRef.current;
    if (!baseData) {
      return;
    }

    let newData: AppData = { ...baseData, stars: Math.max(0, newStars) };

    // Sync wallet balance to handle manual star updates
    newData = syncWalletBalance(newData);

    // Add lastSync timestamp
    newData.lastSync = getCurrentTimestamp();

    dataRef.current = newData;
    setData(currentData => {
      if (!currentData) {
        return currentData;
      }

      let updatedData: AppData = { ...currentData, stars: Math.max(0, newStars) };
      updatedData = syncWalletBalance(updatedData);
      updatedData.lastSync = newData.lastSync;
      return updatedData;
    });

    try {
      await NativeFeatures.saveToNativeStorage('star-chore-data', newData);
      await saveFirebaseData(newData);
      console.log('[useStarData] updateStars - Stars updated and saved successfully to both storages');
    } catch (error) {
      console.error('[useStarData] updateStars - Error saving data:', error);
    }
  }, [saveFirebaseData]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Separate effect for initial data loading and user changes
  useEffect(() => {
    fetchData();
  }, [user, fetchData]);

  // Real-time sync for family data
  useEffect(() => {
    if (!user || !data?.familyId) {
      return;
    }

    console.log('[useStarData] Setting up real-time listener for family:', data.familyId);

    let unsubscribe: (() => void) | null = null;

    try {
      // Set up real-time listener for family data changes
      unsubscribe = listenToFamilyData(data.familyId, (updatedData) => {
        try {
          console.log('[useStarData] Received real-time update from family');

          // Migrate updated data to ensure schema compatibility
          const migratedData = migrateDataSchema(updatedData);

          // Check if the data is newer than what we have locally
          const localSync = data.lastSync ? new Date(data.lastSync).getTime() : 0;
          const remoteSync = migratedData.lastSync ? new Date(migratedData.lastSync).getTime() : 0;

          if (remoteSync > localSync) {
            console.log('[useStarData] Applying remote changes (newer)');
            setData(migratedData);

            // Also update local storage
            (async () => {
              try {
                await NativeFeatures.saveToNativeStorage('star-chore-data', migratedData);
              } catch (error) {
                console.error('[useStarData] Error saving real-time update to native storage:', error);
              }
            })();
          }
        } catch (error) {
          console.error('[useStarData] Error processing real-time update:', error);
        }
      });
    } catch (error) {
      console.error('[useStarData] Error setting up real-time listener:', error);
    }

    return () => {
      console.log('[useStarData] Cleaning up real-time listener');
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          console.error('[useStarData] Error during cleanup:', error);
        }
      }
    };
  }, [user, data?.familyId, listenToFamilyData]);

  // Separate effect for timed tasks, daily rewards, and visibility changes
  useEffect(() => {
    const intervalId = setInterval(() => {
      setData(currentData => {
        if (currentData) {
          let updatedData = checkAndResetTimedTasks(currentData);
          updatedData = checkAndResetDailyRewards(updatedData);
          updatedData = syncWalletBalance(updatedData);
          
          if (updatedData !== currentData) {
            // Add lastSync timestamp
            updatedData.lastSync = getCurrentTimestamp();

            // Use async IIFE to properly handle saving to both storages
            (async () => {
              try {
                await NativeFeatures.saveToNativeStorage('star-chore-data', updatedData);
                await saveFirebaseData(updatedData);
                console.log('[useStarData] Interval - Timed tasks updated and saved successfully to both storages');
              } catch (error) {
                console.error('[useStarData] Interval - Error saving data:', error);
              }
            })();
            return updatedData;
          }
        }
        return currentData;
      });
    }, 900000); // Check every 15 minutes (15 * 60 * 1000 ms)
    
    // Listen for page visibility changes to reload data when page becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        fetchData();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchData, saveFirebaseData]);

  // Perform page load resets - can be called explicitly to force reset check
  const performPageLoadResets = useCallback(async () => {
    if (!data) return;
    
    let currentData = data;
    let hasChanges = false;
    
    // Check for new day (has early return if same day)
    const afterDayCheck = checkAndResetForNewDay(currentData);
    if (afterDayCheck !== currentData) {
      currentData = afterDayCheck;
      hasChanges = true;
    }
    
    // Check timed tasks
    const afterTimedCheck = checkAndResetTimedTasks(currentData);
    if (afterTimedCheck !== currentData) {
      currentData = afterTimedCheck;
      hasChanges = true;
    }
    
    // Check daily rewards
    const afterDailyCheck = checkAndResetDailyRewards(currentData);
    if (afterDailyCheck !== currentData) {
      currentData = afterDailyCheck;
      hasChanges = true;
    }
    
    // Sync wallet balance
    const afterWalletSync = syncWalletBalance(currentData);
    if (afterWalletSync !== currentData) {
      currentData = afterWalletSync;
      hasChanges = true;
    }
    
    // Only save if there were actual changes
    if (hasChanges) {
      try {
        await NativeFeatures.saveToNativeStorage('star-chore-data', currentData);
        await saveFirebaseData(currentData);
        setData(currentData);
        console.log('[useStarData] performPageLoadResets - Resets applied and saved');
      } catch (error) {
        console.error('[useStarData] performPageLoadResets - Error saving reset data:', error);
      }
    }
  }, [data, saveFirebaseData]);

  return {
    data,
    loading,
    setData,
    updateData,
    resetAppData,
    updateStars,
    performPageLoadResets
  };
};
